const controllers = new Map();

const phoneNumber = value => {
  const input = value.trim();
  const digits = input.replace(/\D/g, '');
  if (!/^\+?[\d\s().-]+$/.test(input)) return null;
  if (input.startsWith('+') && /^[1-9]\d{7,14}$/.test(digits)) return '+' + digits;
  if (/^[2-9]\d{2}[2-9]\d{6}$/.test(digits)) return '+1' + digits;
  if (/^1[2-9]\d{2}[2-9]\d{6}$/.test(digits)) return '+' + digits;
  return null;
};

// S205: the email box posts to Klaviyo like the texts box. A dot in the domain is required so a typo like name@gmail is caught here.
const emailAddress = value => {
  const input = value.trim();
  return /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/.test(input) ? input : null;
};

// One Klaviyo signup box: validates on blur and submit, posts the profile to the form's list, shows the success line or a real error.
const bindSignup = ({ footer, form, error, success, hideOnSuccess, parse, profile, signal, cleanup }) => {
  const input = form.querySelector('input');
  const button = form.querySelector('button');
  let request;
  let pending = false;
  let timeout;
  cleanup.push(() => {
    request?.abort();
    clearTimeout(timeout);
  });
  // S166: validate on blur with the same message as submit; clear as soon as a valid value is typed. Never submits.
  input.addEventListener('blur', () => {
    if (pending || !input.value.trim() || parse(input.value)) return;
    error.textContent = form.dataset.invalid;
    error.hidden = false;
    input.setAttribute('aria-invalid', 'true');
  }, { signal });
  input.addEventListener('input', () => {
    if (input.getAttribute('aria-invalid') !== 'true' || !parse(input.value)) return;
    error.hidden = true;
    input.removeAttribute('aria-invalid');
  }, { signal });
  form.addEventListener('submit', async event => {
    event.preventDefault();
    if (pending) return;
    const value = parse(input.value);
    error.hidden = true;
    input.removeAttribute('aria-invalid');
    if (!value) {
      error.textContent = form.dataset.invalid;
      error.hidden = false;
      input.setAttribute('aria-invalid', 'true');
      input.focus({ preventScroll: true });
      return;
    }
    pending = true;
    const restoreButtonFocus = document.activeElement === button;
    button.disabled = true;
    input.readOnly = true;
    form.setAttribute('aria-busy', 'true');
    request = new AbortController();
    timeout = setTimeout(() => request.abort(), 20000);
    try {
      if (!form.dataset.companyId || !form.dataset.listId) throw new Error('Missing signup destination');
      const response = await fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(form.dataset.companyId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/vnd.api+json', Accept: 'application/vnd.api+json', revision: '2025-07-15' },
        body: JSON.stringify({ data: {
          type: 'subscription',
          attributes: { profile: { data: { type: 'profile', attributes: profile(value) } } },
          relationships: { list: { data: { type: 'list', id: form.dataset.listId } } }
        } }),
        signal: request.signal
      });
      if (response.status !== 202) throw new Error('Signup not accepted');
      if (!footer.isConnected) return;
      hideOnSuccess.forEach(element => { if (element) element.hidden = true; });
      success.hidden = false;
      success.focus({ preventScroll: true });
    } catch {
      if (!footer.isConnected) return;
      error.textContent = form.dataset.error;
      error.hidden = false;
    } finally {
      clearTimeout(timeout);
      pending = false;
      button.disabled = false;
      input.readOnly = false;
      form.removeAttribute('aria-busy');
      if (!error.hidden && restoreButtonFocus && footer.isConnected) button.focus({ preventScroll: true });
    }
  }, { signal });
};

const initialize = root => {
  root.querySelectorAll('.sap-kit-footer--3').forEach(footer => {
    if (controllers.has(footer)) return;
    const listeners = new AbortController();
    const cleanup = [];
    controllers.set(footer, () => {
      listeners.abort();
      cleanup.forEach(dispose => dispose());
    });
    // S168: mark the first item of each legal row line (no divider, no left pad) and the last (no right pad); re-marked when the row reflows.
    const legal = footer.querySelector('.sap-kit-footer__legal');
    const legalItems = legal ? [...legal.querySelectorAll('.sap-kit-footer__copyright, .sap-kit-footer__legal-links a')] : [];
    const markLines = () => {
      let previous = null;
      legalItems.forEach((item, index) => {
        const box = item.getBoundingClientRect();
        const centre = box.top + box.height / 2;
        const starts = index === 0 || centre > previous + 6;
        item.toggleAttribute('data-line-start', starts);
        if (index > 0) legalItems[index - 1].toggleAttribute('data-line-end', starts);
        previous = centre;
      });
      if (legalItems.length) legalItems[legalItems.length - 1].toggleAttribute('data-line-end', true);
    };
    if (legalItems.length) {
      markLines();
      const watch = new ResizeObserver(markLines);
      watch.observe(legal);
      legalItems.forEach(item => watch.observe(item));
      listeners.signal.addEventListener('abort', () => watch.disconnect());
      // WebKit applies a swapped font a frame after its load events settle, so re-mark two frames later.
      const remark = () => requestAnimationFrame(() => requestAnimationFrame(() => { if (footer.isConnected) markLines(); }));
      document.fonts?.ready.then(remark);
      document.fonts?.addEventListener('loadingdone', remark, { signal: listeners.signal });
      window.addEventListener('resize', markLines, { signal: listeners.signal, passive: true });
      window.addEventListener('load', remark, { signal: listeners.signal });
    }
    const emailForm = footer.querySelector('[data-footer-email-form]');
    if (emailForm) bindSignup({
      footer,
      form: emailForm,
      error: emailForm.querySelector('[data-footer-email-error]'),
      success: emailForm.querySelector('[data-footer-email-success]'),
      hideOnSuccess: [emailForm.querySelector('.sap-kit-footer__field')],
      parse: emailAddress,
      profile: email => ({ email, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } }),
      signal: listeners.signal,
      cleanup
    });
    const opener = footer.querySelector('[data-footer-sms-open]');
    const panel = footer.querySelector('.sap-kit-footer__sms');
    opener?.addEventListener('click', () => {
      panel.hidden = false;
      opener.setAttribute('aria-expanded', 'true');
      opener.hidden = true;
      panel.querySelector('input')?.focus({ preventScroll: true });
    }, { signal: listeners.signal });
    const form = footer.querySelector('[data-footer-sms-form]');
    if (form) bindSignup({
      footer,
      form,
      error: panel.querySelector('[data-footer-sms-error]'),
      success: panel.querySelector('[data-footer-sms-success]'),
      hideOnSuccess: [form, panel.querySelector('.sap-kit-footer__consent')],
      parse: phoneNumber,
      profile: phone_number => ({ phone_number, subscriptions: { sms: { marketing: { consent: 'SUBSCRIBED' } } } }),
      signal: listeners.signal,
      cleanup
    });
  });
};

initialize(document);
document.addEventListener('shopify:section:load', event => initialize(event.target));
document.addEventListener('shopify:section:unload', event => {
  for (const [footer, dispose] of controllers) {
    if (event.target.contains(footer)) {
      dispose();
      controllers.delete(footer);
    }
  }
});
